import ClickWrapper, { type ClickWrapperProps } from '@/components/ClickWrapper'
import Tooltip from '@/components/Tooltip'
import { READABILITY_EXTRACT } from '@/constants/api'
import type { LinkInfo } from '@/states/attachment'
import { post } from '@/utils/request'
import { css } from '@emotion/react'
import { Button, Input } from '@ruguoapp/jike-fe-components'
import { LinkIcon } from '@ruguoapp/jike-fe-icons'
import { isEmpty } from 'lodash-es'
import { useCallback, useState } from 'react'

export interface LinkAttachmentPickerProps extends Omit<ClickWrapperProps, 'onSelect'> {
	onSelect?: (link: LinkInfo) => void
	disabled?: boolean
}

export default function LinkAttachmentPicker({
	disabled = false,
	onSelect,
	...props
}: LinkAttachmentPickerProps) {
	const [content, setContent] = useState<string>('')
	const [isShow, setIsShow] = useState<boolean>(false)

	const [isLoading, setIsLoading] = useState<boolean>(false)
	const parseLinkInfo = useCallback(() => {
		if (isEmpty(content)) {
			return
		}
		setIsLoading(true)
		post<{ data: LinkInfo }>(READABILITY_EXTRACT, {
			link: content,
		})
			.then((res) => res.data.data)
			.then((data) => void onSelect?.(data))
			.then(() => {
				setContent('')
				setIsShow(false)
			})
			.finally(() => {
				setIsLoading(false)
			})
	}, [content, onSelect])

	return (
		<Tooltip
			opened={isShow}
			onClose={() => setIsShow(false)}
			withArrow
			offset={10}
			position="bottom"
			styles={{
				dropdown: {
					backgroundColor: 'var(--bg-on-body-1)',
					padding: '20px',
					boxShadow: '0 10px 40px var(--solid-shadow-1)',
					borderRadius: '8px',
					width: '400px',
				},
				arrow: {
					backgroundColor: 'var(--bg-on-body-1)',
				},
			}}
			content={
				<div
					css={css`
						display: flex;
						flex-direction: column;
						gap: 10px;
					`}
				>
					<header
						css={css`
							display: flex;
							justify-content: space-between;
							align-items: center;
						`}
					>
						<h1
							css={css`
								font-size: 18px;
								line-height: 24px;
								margin: 0;
							`}
						>
							添加链接
						</h1>
						<Button appearance="ghost" size="sm" onClick={parseLinkInfo} loading={isLoading}>
							添加
						</Button>
					</header>
					<div
						css={css`
							font-size: 14px;
							line-height: 20px;
							color: var(--tint-secondary);
						`}
					>
						可以添加微信、微博、知乎、秒拍、爱奇艺等各类网站链接，支持主流视频网站嵌入播放
					</div>
					<Input
						value={content}
						onChange={setContent}
						autoFocus
						css={css`
							background-color: var(--bg-surface-body-1);
							padding: 5px 10px;
							border-radius: 8px;

							.jk-form-input {
								height: 30px;
							}
						`}
					/>
				</div>
			}
		>
			<ClickWrapper
				css={css`
					display: flex;
					gap: 5px;
				`}
				onClick={(e) => {
					e.stopPropagation()
					if (disabled) {
						return
					}
					setIsShow((prev) => !prev)
				}}
				{...props}
			>
				<LinkIcon
					css={css`
						width: 20px;
						height: 20px;
						${disabled
							? css`
									cursor: not-allowed;
									color: var(--tint-icon-gray);
								`
							: 'color: var(--tint-jike-blue);'}
					`}
				/>
			</ClickWrapper>
		</Tooltip>
	)
}
